import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { acss } from '@adornis/chemistry/directives/acss.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-chip.js';
import type { Renderable } from '@adornis/chemistry/renderable.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { getCompanyInfo } from '@ils/config/project-config.js';
import { ExamType } from '@ils/static/enums.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { ExamDay } from '../../db/examdays.js';
import type { ILSUser } from '../../db/ils-user.js';
import './ils-product-details-card.js';
import './ils-product-flow-header.js';

@customElement('ils-buy-selected-product')
export class ILSBuySelectedProduct extends ChemistryLitElement {
  @property() private readonly examDay!: ExamDay;
  @property() private readonly processingOrder: boolean = false;

  @state() private readonly user = new RXController(this, currentUser<ILSUser>());

  override render() {
    if (!this.user.value) return nothing;
    return html`
      <x-flex space="md" ${css({ height: '100%' })}>
        <ils-product-flow-header .examDay=${this.examDay}> </ils-product-flow-header>
        <x-flex horizontal wrap space="md">
          ${this._renderUserDetails()}
          <ils-product-details-card
            ${acss({
              flex: 1,
              '@media (max-width: 600px)': {
                width: '100%',
                maxWidth: 'none',
              },
            })}
            .examDay=${this.examDay}
          >
          </ils-product-details-card>
        </x-flex>
        <ils-signup-missing ${css({ alignSelf: 'flex-end' })} .user=${this.user.value}></ils-signup-missing>
        ${this._renderBuyButton()}
      </x-flex>
    `;
  }

  protected _renderUserDetails(): Renderable {
    if (!this.user.value) return nothing;
    const firstName = this.user.value.ecommerce?.firstName;
    const lastName = this.user.value.ecommerce?.lastName;
    const gender = this.user.value.profile?.gender;
    const email = this.user.value.email;
    return html`
      <x-basic-card padding="md" flex ${css({ minWidth: '300px' })}>
        <x-flex space="sm">
          <x-flex horizontal space-between>
            <x-h4> ${firstName} ${lastName} </x-h4>
            <x-icon>person</x-icon>
          </x-flex>
          <x-text> Geschlecht: ${gender} </x-text>
          <x-text> E-Mail: ${email} </x-text>
          <x-text bold>
            Sollten das nicht Sie sein,
            <x-link
              ${css({ display: 'inline-flex', color: this.colors.accent })}
              @click="${() =>
                this.dispatchEvent(new CustomEvent('logout', { bubbles: true, cancelable: true, composed: true }))}"
            >
              loggen Sie sich hier aus!
            </x-link>
          </x-text>
        </x-flex>
      </x-basic-card>
    `;
  }

  protected _renderBuyButton(): Renderable {
    if (!this.user.value) return nothing;
    const isUserInCandidates = this.examDay.candidateIDs.includes(this.user.value._id);
    const price = +(this.examDay.price / 100).toFixed(2);
    const taxPercent = getCompanyInfo(this.examDay.examType ?? ExamType.FAMED).taxAmountPercent;
    const tax = +taxPercent ? ` (inkl. ${taxPercent}% MwSt.)` : '';
    return html`
      <x-flex space="sm">
        <x-flex horizontal end crossaxis-center padding="xs">
          <x-text bold ${css({ fontSize: '1.2em' })}> Gesamtbetrag: ${html`${price} &euro;${tax}`} </x-text>
        </x-flex>
        <x-flex horizontal end>
          ${isUserInCandidates
            ? html`<x-button disabled> Bereits gekauft </x-button>`
            : html`
                <x-button
                  ${acss({
                    '@media (max-width: 600px)': {
                      width: '100%',
                      maxWidth: 'none',
                    },
                  })}
                  validate
                  ?disabled=${!!this.user.value.missingProperties().length}
                  @click="${() =>
                    this.dispatchEvent(
                      new CustomEvent('buy', {
                        bubbles: true,
                        cancelable: true,
                        composed: true,
                      }),
                    )}"
                >
                  ${this.processingOrder ? 'Bezahlung verarbeiten...' : 'Jetzt kaufen'}
                </x-button>
              `}
        </x-flex>
      </x-flex>
    `;
  }
}
